<script lang="ts" setup>
import { getCmsImage } from '@/composables/useCms';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import { useMediaQuery } from '@vueuse/core';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

interface CmsCallToAction {
    label?: string;
    url?: string;
    target?: string;
    type?: string;
    elementId?: string;
    elementTitle?: string;
}

interface CmsProduct {
    id: string;
    title: string;
    images?: { url: string; title: string }[];
    price?: string;
    newPrice?: string;
    callToAction?: CmsCallToAction;
}

defineProps<{
    title?: string;
    categoryImage?: { url: string; title: string }[];
    products?: CmsProduct[];
}>();

const isMobile = useMediaQuery('(max-width: 1023px)');
</script>

<template>
    <div class="py-8">
        <VContainer>
            <Card class="flex flex-col lg:flex-row overflow-hidden">
                <!-- Left: category image with zoom-out hover effect -->
                <div class="group w-full lg:w-1/2 shrink-0 aspect-square overflow-hidden">
                    <NuxtImg
                        v-if="categoryImage?.length"
                        :src="getCmsImage(categoryImage)"
                        :alt="categoryImage[0]?.title || title"
                        format="webp"
                        loading="lazy"
                        class="w-full h-full object-center object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                </div>

                <!-- Right: title + products -->
                <div class="w-full lg:w-1/2 flex flex-col p-6 lg:px-24 overflow-hidden">
                    <h2 v-if="title" class="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 shrink-0">
                        {{ title }}
                    </h2>

                    <!-- Desktop: 2×2 grid — entire card is the CTA -->
                    <div v-if="products?.length && !isMobile" class="grid grid-cols-2 gap-4 grow min-h-0">
                        <NuxtLink
                            v-for="product in products.slice(0, 4)"
                            :key="product.id"
                            :to="product.callToAction?.url || '#'"
                            :target="product.callToAction?.target || '_self'"
                            class="group/card"
                        >
                            <Card class="flex flex-col overflow-hidden h-full transition-shadow duration-200 group-hover/card:shadow-lg group-hover/card:border-gray-400 cursor-pointer">
                                <div class="relative shrink-0 bg-gray-50 overflow-hidden">
                                    <NuxtImg
                                        v-if="product.images?.length"
                                        :src="getCmsImage(product.images)"
                                        :alt="product.title"
                                        format="webp"
                                        loading="lazy"
                                        class="w-full h-full object-center object-cover transition-transform duration-700 group-hover/card:scale-105"
                                    />
                                </div>
                                <CardHeader class="p-3 pb-1">
                                    <CardTitle class="text-sm font-semibold line-clamp-2 group-hover/card:text-primary transition-colors">
                                        {{ product.title }}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent class="p-3 pt-0 mt-auto">
                                    <div class="flex items-baseline gap-2">
                                        <span
                                            class="font-bold"
                                            :class="product.newPrice ? 'text-sm text-gray-400 line-through' : 'text-sm text-gray-900'"
                                        >{{ product.price }}</span>
                                        <span v-if="product.newPrice" class="text-xl font-bold text-gray-900">
                                            {{ product.newPrice }}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </NuxtLink>
                    </div>

                    <!-- Mobile: swiper — entire card is the CTA -->
                    <Swiper
                        v-else-if="products?.length && isMobile"
                        :slides-per-view="1.2"
                        :space-between="16"
                        :pagination="{ clickable: true }"
                        :modules="[Pagination]"
                        class="w-full pb-10!"
                    >
                        <SwiperSlide v-for="product in products" :key="product.id">
                            <NuxtLink
                                :to="product.callToAction?.url || '#'"
                                :target="product.callToAction?.target || '_self'"
                                class="group/card block h-full"
                            >
                                <Card class="flex flex-col overflow-hidden h-full transition-shadow duration-200 group-hover/card:shadow-lg cursor-pointer">
                                    <div class="relative h-48 bg-gray-50 overflow-hidden">
                                        <NuxtImg
                                            v-if="product.images?.length"
                                            :src="getCmsImage(product.images)"
                                            :alt="product.title"
                                            format="webp"
                                            loading="lazy"
                                            class="w-full h-full object-center object-cover transition-transform duration-700 group-hover/card:scale-105"
                                        />
                                    </div>
                                    <CardHeader>
                                        <CardTitle class="text-base font-semibold line-clamp-2 group-hover/card:text-primary transition-colors">
                                            {{ product.title }}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent class="mt-auto">
                                        <div class="flex items-baseline gap-3">
                                            <span
                                                class="font-bold"
                                                :class="product.newPrice ? 'text-sm text-gray-400 line-through' : 'text-lg text-gray-900'"
                                            >{{ product.price }}</span>
                                            <span v-if="product.newPrice" class="text-xl font-bold text-gray-900">
                                                {{ product.newPrice }}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </NuxtLink>
                        </SwiperSlide>
                    </Swiper>
                </div>
            </Card>
        </VContainer>
    </div>
</template>
